import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { StepperModule } from 'primeng/stepper';
import { ToastModule } from 'primeng/toast';
import { FileUpload } from 'primeng/fileupload';
import { MessageService } from 'primeng/api';
import { HttpErrorResponse } from '@angular/common/http';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { MessageModule } from 'primeng/message';
import { FormFooter } from "src/app/shared/components/form-footer/form-footer";
import { ActivatedRoute, Router } from '@angular/router';
import { Routes } from 'src/app/core/routes/routes';
import { TextareaModule } from 'primeng/textarea';
import { BusinessApi } from '@features/business/services/business-api';
import { DataEndpoint } from '@core/models/general.model';
import { BusinessCreateRequestModel, BusinessDetailModel } from '@features/business/models/business.model';
import { FormUtils } from '@shared/utils/form.utils';
import { BlockUI } from "primeng/blockui";
import { ProgressSpinner } from "primeng/progressspinner";
import { AppDialog } from "@shared/components/dialog";
import { BusinessStateService } from '@core/services/business-state.service';

@Component({
  selector: 'app-business-group-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    InputTextModule,
    TextareaModule,
    InputNumberModule,
    ButtonModule,
    MessageModule,
    StepperModule,
    FileUpload,
    ToastModule,
    FormFooter,
    BlockUI,
    ProgressSpinner,
    AppDialog
],
  templateUrl: './business-group-form.html',
  styleUrl: './business-group-form.scss',
  providers: [MessageService]
})
export class BusinessGroupForm {
  readonly router = inject(Router);
  readonly route = inject(ActivatedRoute);
  readonly messageService = inject(MessageService);
  readonly businessApi = inject(BusinessApi);
  private readonly businessStateService = inject(BusinessStateService);

  isEditMode = signal(false);

  form: FormGroup = new FormGroup({
    logo: new FormControl(null, [Validators.required]),
    name: new FormControl('', [Validators.required, Validators.maxLength(255)]),
    description: new FormControl('', [Validators.required, Validators.maxLength(1000)]),
    contactName: new FormControl('', [Validators.required, Validators.maxLength(255)]),
    contactPhone: new FormControl('', [Validators.required, Validators.maxLength(100)]),
    contactEmail: new FormControl('', [Validators.required, Validators.email]),
    maxUsers: new FormControl(1, [Validators.required, Validators.pattern('^[0-9]*$')])
  });

  businessGroupId: string | null = null;
  isFormSubmitted = false;
  isLoading = false;
  activeStep: number = 1;
  uploadedFiles: any[] = [];
  fileUrl: string = '';
  fileName: string = '';

  visibleSuccessDialog: boolean = false;
  dialogTitle: string = '';
  dialogMessage: string = '';

  ngOnInit(): void {
    this.checkMode();
  }

  private checkMode(): void {
    this.businessGroupId = this.route.snapshot.paramMap.get('id');

    if (this.businessGroupId) {
      this.isEditMode.set(true);
      this.loadBusinessGroupData(this.businessGroupId);
    } else {
      this.isEditMode.set(false);
    }
  }

  private loadBusinessGroupData(id: string): void {
    this.isLoading = true;

    this.businessApi.getBusinessDetail(id).subscribe({
      next: (response: DataEndpoint<BusinessDetailModel>) => {
        if (response.Status === 200) {
          this.setEditForm(response.Data!);
        }
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error?.error.Message,
          life: 3000
        });
        this.isLoading = false;
      }
    });
  }

  private setEditForm(data: BusinessDetailModel) {
    this.form.patchValue({
      logo: data.LogoUrl,
      name: data.Name,
      description: data.Description,
      contactName: data.ContactName,
      contactPhone: data.ContactPhone,
      contactEmail: data.ContactEmail,
      maxUsers: data.MaxUsers
    })
    this.fileUrl = data.LogoUrl;
    this.fileName = data.Logo;
  }

   // Validación del formulario antes de avanzar
   canGoNext(): boolean {
    return this.form.valid;
  }

  // Avanzar al siguiente paso
  onClickNextStep(): void {
    if (!this.canGoNext()) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.activeStep < 2) {
      this.onSaveForm();
    }
  }

  // Retroceder al paso anterior
  onClickPreviousStep(): void {
    if (this.activeStep > 1) {
      this.activeStep--;
    }
  }

  onClickCancel(): void {
    this.router.navigate([Routes.businessesPage]);
  }

  // Helpers para deshabilitar botones
  isFirstStep(): boolean {
    return this.activeStep === 1;
  }

  isLastStep(): boolean {
    return this.activeStep === 2;
  }

  onSelectFile(event: any) {
    for (let file of event.files) {
      this.uploadedFiles.push(file);
      this.form.patchValue({logo: file});
      this.fileUrl = file.objectURL;
      this.fileName = file.name;
    }
  }

  removeLogo() {
    this.form.get('logo')?.setValue('')
    this.fileUrl = '';
    this.fileName = '';
  }

  showCreateSuccessDialog() {
    this.dialogTitle = this.isEditMode()
      ? '¡Grupo empresarial actualizado!'
      : '¡Grupo empresarial creado!';
    this.dialogMessage = this.isEditMode()
      ? 'El grupo empresarial ha sido actualizado exitosamente.'
      : 'El grupo empresarial ha sido creado exitosamente.';
    this.visibleSuccessDialog = true;
  }

  onSaveForm() {
    if (this.form.valid) {
      const request: BusinessCreateRequestModel = {
        IsGroup: true,
        Name: this.form.get('name')?.value,
        ContactName: this.form.get('contactName')?.value,
        ContactPhone: this.form.get('contactPhone')?.value,
        ContactEmail: this.form.get('contactEmail')?.value,
        Description: this.form.get('description')?.value,
        MaxUsers: this.form.get('maxUsers')?.value,
        Logo: this.form.get('logo')?.value
      }
      const formData = FormUtils.toFormData(request);

      this.isLoading = true;

      let requestUrl = this.businessApi.postBusiness(formData);
      if (this.isEditMode() && this.businessGroupId) {
        requestUrl = this.businessApi.updateBusiness(this.businessGroupId, formData);
      }

      requestUrl.subscribe({
        next: (response: DataEndpoint<any>) => {
          if (response.Success) {
            this.businessStateService.refreshBusinessList();
            this.showCreateSuccessDialog();
            this.activeStep++;
          }
          this.isLoading = false;
        },
        error: (error: HttpErrorResponse) => {
          this.isLoading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: error?.error.Message });
        }
      })
    }
    else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'El formulario es inválido. Por favor, revisar.' });
    }
  }

  onCloseDialog() {
    this.visibleSuccessDialog = false;
    this.router.navigate([Routes.businessesPage]);
  }

  isInvalid(controlName: string) {
    const control = this.form.get(controlName);
    return control?.invalid && (control.touched || this.isFormSubmitted);
  }

  getErrorMessage(controlName: string): string {
    const control = this.form.get(controlName);
    return FormUtils.getErrorMessage(control);
  }
}
